<template>
  <div>
    <v-row>
      <v-col
        cols="12"
        sm="12"
      >
        <v-expansion-panels
          class="mb-6"
          :value="0"
        >
          <v-expansion-panel>
            <v-expansion-panel-header>{{ $t('Additional languages') }}</v-expansion-panel-header>
            <v-expansion-panel-content>
              <p class="body-2">
                {{ $t("This is where you manage your FAQ's additional languages and translations") }}
              </p>
              <faq-languages-manage
                :faq-id="faq.id"
                :available-faq-languages="availableFaqLanguages"
                :faq-languages="faqLanguages"
                @language:created="handleLanguageCreated"
              />
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>

        <v-alert
          v-if="faqLanguages.length > 0"
          :color="isMissingTranslations ? 'orange' : 'green'"
          :icon="isMissingTranslations ? 'mdi-alert-circle' : 'mdi-check-circle'"
          dark
        >
          <template v-if="isMissingTranslations">
            {{ $t('Your FAQ is missing translations') }}
          </template>
          <template v-else>
            {{ $t('Great! Your FAQ is fully translated in your additional languages') }}
          </template>
        </v-alert>

        <v-expansion-panels v-model="translationPanel">
          <v-expansion-panel :disabled="faqLanguages.length === 0">
            <v-expansion-panel-header class="justify-self-start">
              <div class="d-flex align-center">
                <v-icon
                  v-if="faqLanguages.length > 0"
                  :color="missingTranslations.flags.faq || missingTranslations.flags.contact_form_fields ? 'orange' : 'green'"
                  class="pr-8"
                  v-text="missingTranslations.flags.faq || missingTranslations.flags.contact_form_fields ? 'mdi-alert-circle' : 'mdi-check-circle'"
                />
                <span>{{ $t('Labels and texts') }}</span>
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <p class="body-2">
                {{ $t('Translations for the texts from the settings view: Search placeholder text, Search results text etc.') }}
              </p>

              <faq-translations-list
                :translations="faq.translations"
                :missing-translations="missingTranslations"
                :all-languages="faq.all_languages"
                :faq-id="faq.id"
                :contact-form-fields="faq.contact_form_fields"
              />
            </v-expansion-panel-content>
          </v-expansion-panel>

          <v-expansion-panel :disabled="faqLanguages.length === 0 || categories.length === 0">
            <v-expansion-panel-header>
              <div class="d-flex align-center">
                <v-icon
                  v-if="faqLanguages.length > 0"
                  :color="missingTranslations.flags.categories ? 'orange' : 'green'"
                  class="pr-8"
                  v-text="missingTranslations.flags.categories ? 'mdi-alert-circle' : 'mdi-check-circle'"
                />
                <span>{{ $t('Categories') }}</span>
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <p class="body-2">
                {{ $t('Manage translations for your categories') }}
              </p>
              <category-translations-list
                :faq-id="faq.id"
                :missing-translations="missingTranslations"
                :models="categories"
              />
            </v-expansion-panel-content>
          </v-expansion-panel>

          <v-expansion-panel :disabled="faqLanguages.length === 0 || subcategories.length === 0">
            <v-expansion-panel-header>
              <div class="d-flex align-center">
                <v-icon
                  v-if="faqLanguages.length > 0"
                  :color="missingTranslations.flags.subcategories ? 'orange' : 'green'"
                  class="pr-8"
                  v-text="missingTranslations.flags.subcategories ? 'mdi-alert-circle' : 'mdi-check-circle'"
                />
                <span>{{ $t('Subcategories') }}</span>
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <p class="body-2">
                {{ $t('Manage translations for your subcategories') }}
              </p>
              <category-translations-list
                :faq-id="faq.id"
                :models="subcategories"
                :missing-translations="missingTranslations"
                :is-subcategories="true"
              />
            </v-expansion-panel-content>
          </v-expansion-panel>

          <v-expansion-panel :disabled="faqLanguages.length === 0 || questions.length === 0">
            <v-expansion-panel-header>
              <div class="d-flex align-center">
                <v-icon
                  v-if="faqLanguages.length > 0"
                  :color="missingTranslations.flags.questions ? 'orange' : 'green'"
                  class="pr-8"
                  v-text="missingTranslations.flags.questions ? 'mdi-alert-circle' : 'mdi-check-circle'"
                />
                <span>{{ $t('Questions') }}</span>
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-row align="center">
                <v-col
                  class="pt-0"
                  cols="12"
                  sm="6"
                >
                  <p class="mb-0 body-2">
                    {{ $t('Manage translations for your questions & answers') }}
                  </p>
                </v-col>
                <v-col
                  class="pt-0"
                  cols="12"
                  sm="6"
                >
                  <v-text-field
                    v-model="searchQuestion"
                    class="mb-4"
                    append-icon="mdi-magnify"
                    :label="$t('Search')"
                    single-line
                    hide-details
                  />
                </v-col>
              </v-row>
              <question-translations-table
                :faq-id="faq.id"
                :search="searchQuestion"
                :questions="questions"
                :categories="categoriesKeyedById"
                :subcategories="subcategoriesKeyedById"
                :missing-translations="missingTranslations"
              />
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import keyBy from 'lodash/keyBy';
import FaqLanguagesManage from '@/components/faq/FaqLanguagesManage';
import FaqTranslationsList from '@/components/faq/FaqTranslationsList';
import CategoryTranslationsList from '@/components/faq/CategoryTranslationsList';
import QuestionTranslationsTable from '@/components/faq/QuestionTranslationsTable';
import Layout from '@/components/shared/TheLayout';
import TabsLayout from '@/components/faq/TabsLayout';

export default {
  components: {
    CategoryTranslationsList,
    FaqTranslationsList,
    QuestionTranslationsTable,
    FaqLanguagesManage
  },

  layout: (h, page) => h(Layout, {
    props: {
      noWrappingContainer: true,
      pageTitle: page.data.props.faq.default_translation.name
    }
  }, [h(TabsLayout, {
    props: {
      activeTab: 'translations',
      faq: page.data.props.faq,
      forceHidePreview: true
    }
  }, [page])]),

  props: {
    faq: {
      type: Object,
      required: true
    },
    questions: {
      type: Array,
      required: true
    },
    categories: {
      type: Array,
      required: true
    },
    subcategories: {
      type: Array,
      required: false,
      default: () => []
    },
    faqLanguages: {
      type: Array,
      required: true
    },
    availableFaqLanguages: {
      type: Array,
      required: true
    },
    missingTranslations: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      searchQuestion: '',
      translationPanel: undefined
    }
  },

  computed: {
    isMissingTranslations() {
      return (
        this.missingTranslations.flags.faq ||
        this.missingTranslations.flags.categories ||
        this.missingTranslations.flags.questions ||
        this.missingTranslations.flags.contact_form_fields
      )
    },

    categoriesKeyedById() {
      return keyBy(this.categories, 'id');
    },

    subcategoriesKeyedById() {
      return keyBy(this.subcategories, 'id');
    }
  },

  methods: {
    handleLanguageCreated() {
      this.$nextTick(() => {
        this.translationPanel = 0;
      });
    }
  }
}
</script>

<style scoped>
.wrap-text {
  white-space: normal;
}
</style>